import { ReactNode, useEffect } from 'react';
import { Slide, ToastContainer, TypeOptions, toast } from 'react-toastify';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';

import { classNames } from '../utils';

import styles from './ToastProvider.module.css';

function hasVisibleToast() {
  return Boolean(document.querySelector('.Toastify__toast-container '));
}

export function ToastProvider() {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!hasVisibleToast()) return;
      toast.dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <ToastContainer
      pauseOnHover
      autoClose={false}
      hideProgressBar
      theme="dark"
      closeButton
      transition={Slide}
      toastClassName={({ type }) =>
        classNames(styles.toast, type && styles[type])
      }
      position="top-center"
      icon={({ type }) => {
        switch (type) {
          case 'error':
            return (
              <FontAwesomeIcon name="hexagon-exclamation" type="regular" />
            );
          case 'warning':
            return <FontAwesomeIcon name="circle-exclamation" type="regular" />;
          case 'success':
            return <FontAwesomeIcon name="circle-check" type="regular" />;
          case 'info':
            return <FontAwesomeIcon name="circle-info" type="regular" />;
          default:
            return null;
        }
      }}
    />
  );
}

const autoCloseByType: Record<TypeOptions, number | false> = {
  success: 5000,
  info: 6000,
  warning: 9000,
  error: 9000,
  default: 5000,
};

export const showToast = (
  type: TypeOptions,
  message: ReactNode,
  action?: { name: string; fnc: () => void },
) => {
  const content = action
    ? ({ closeToast }) => (
        <div className={styles.toastContent}>
          <span className={styles.toastMessage}>{message}</span>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => {
              closeToast?.();
              // Defer so toast closes before heavy action handlers
              setTimeout(() => action.fnc(), 0);
            }}
          >
            {action.name}
          </button>
        </div>
      )
    : message;

  toast(content, {
    type,
    autoClose: action ? false : autoCloseByType[type],
  });
};
